import React from "react";
import { MdVerified, MdOutlineAccessTime, MdLocationOn } from "react-icons/md";
import { FiCheck, FiCreditCard } from "react-icons/fi";
import { FiMapPin, FiPhone, FiMail } from "react-icons/fi";
import { PiWalletFill } from "react-icons/pi"; // Wallet-style icon from phosphor-react
import { FaApple, FaApplePay } from "react-icons/fa";
import { SiGooglepay } from "react-icons/si";

const AboutSection = ({ operator }) => {
  if (!operator) return null;

  const {
    name,
    bio,
    address,
    town,
    country,
    phone,
    email,
    additionalInfo,
    openingHours,
  } = operator;

  const encodedAddress = encodeURIComponent(`${address}, ${town}, ${country}`);
  const mapEmbedLink = `https://www.google.com/maps?q=${encodedAddress}&output=embed`;

  return (
    <section className="max-w-5xl mx-auto px-4 py-12">
      {/* About Header */}
      <h2 className="text-3xl font-bold mb-4">About</h2>

      {/* Description */}
      <p className="text-textGray mb-8 leading-relaxed ">{bio}</p>
      <div className="mb-6 flex flex-col gap-y-3 text-sm text-textGray leading-relaxed">
        <p className="flex items-start gap-2">
          <FiMapPin className="mt-[2px] w-5 h-5 text-redlight shrink-0" />
          <span>
            {address}, {town}, {country}
          </span>
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`}
            className="text-blue-500 underline ml-2"
            target="_blank"
            rel="noopener noreferrer"
          >
            Get directions
          </a>
        </p>
        <p className="flex items-start gap-2">
          <FiPhone className="mt-1 text-base text-redlight" />
          <span>{operator?.phone}</span>
        </p>
        <p className="flex items-start gap-2">
          <FiMail className="mt-1 text-base text-redlight" />
          <span>{operator?.email}</span>
        </p>
        <p className="text-xs text-gray-400 mb-8">
          Nearby {town}, {country}
        </p>
      </div>

      {/* Map Embed */}
      <div className="w-full h-[300px] mb-4 rounded overflow-hidden shadow">
        <iframe
          src={mapEmbedLink}
          width="100%"
          height="100%"
          loading="lazy"
          title={`${name} Map`}
        ></iframe>
      </div>

      {/* Location and Nearby */}

      {/* Info Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        {/* Opening Times */}
        <div>
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <MdOutlineAccessTime className="text-redlight" />
            Opening times
          </h3>
          <ul className="text-sm text-textGray space-y-2">
            {openingHours?.map(({ day, slots }) => {
              const formatted =
                slots?.length > 0
                  ? slots.map((s) => `${s.open} – ${s.close}`).join(", ")
                  : "Closed";

              return (
                <li
                  key={day}
                  className={`flex justify-between ${
                    slots?.length ? "text-text" : "text-gray-400"
                  }`}
                >
                  <span className="capitalize">{day}</span>
                  <span className={day === "Saturday" ? "font-semibold" : ""}>
                    {formatted}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Additional Information */}
        <div>
          <h3 className="text-lg font-semibold mb-3">Additional information</h3>
          <ul className="text-sm text-textGray space-y-3">
            {additionalInfo?.verified && (
              <li className="flex items-center gap-2">
                <MdVerified className="text-green-500" />
                Verified by Lokalai
              </li>
            )}
            {additionalInfo?.instantConfirmation && (
              <li className="flex items-center gap-2">
                <FiCheck className="text-green-500" />
                Bookings confirmed instantly
              </li>
            )}
            {additionalInfo?.payByApp && (
              <li className="flex items-center gap-2 text-sm text-text">
                <PiWalletFill className="text-green-500 text-lg" />
                Pay easily via
                <span className="flex items-center gap-1">
                  <FaApple className="text-white text-base" />
                  <span className="text-[#0f0f0f] dark:text-white">Pay</span>
                </span>
                <span className="mx-1">or</span>
                <span className="flex items-center gap-1">
                  <SiGooglepay className="text-[#4285F4] text-white text-3xl" />
                </span>
              </li>
            )}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
